// Client helper to call the server-side Anthropic proxy.
// Always use this from the browser instead of calling api.anthropic.com directly.

import { supabase } from './supabase';

/**
 * Non-streaming call. Returns the parsed JSON response.
 */
export async function anthropic({ model, max_tokens, system, messages, thinking, output_config }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required. Please sign in to use this feature.');
  }

  const body = { model, max_tokens, system, messages };
  if (thinking) body.thinking = thinking;
  if (output_config) body.output_config = output_config;

  const response = await fetch('/api/anthropic', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify(body)
  });

  const text = await response.text();
  if (!response.ok) {
    if (response.status === 401) throw new Error('Session expired. Please sign in again.');
    if (response.status === 429) throw new Error('Too many requests. Please wait a moment and try again.');
    throw new Error(text || `Anthropic proxy error: ${response.status}`);
  }

  try { return JSON.parse(text); } catch { return text; }
}

/**
 * Streaming call. Parses Anthropic's SSE event stream and invokes callbacks.
 *
 * @param {object} params — model, max_tokens, system, messages, thinking, output_config
 * @param {object} handlers
 * @param {(deltaText: string) => void} handlers.onText — called for every text delta
 * @param {(fullText: string) => void} [handlers.onComplete] — called once with the full text at end
 * @returns {Promise<string>} full concatenated text
 */
export async function anthropicStream(
  { model, max_tokens, system, messages, thinking, output_config },
  { onText, onComplete } = {}
) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required. Please sign in to use this feature.');
  }

  const body = { model, max_tokens, system, messages, stream: true };
  if (thinking) body.thinking = thinking;
  if (output_config) body.output_config = output_config;

  const response = await fetch('/api/anthropic', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const text = await response.text();
    if (response.status === 401) throw new Error('Session expired. Please sign in again.');
    if (response.status === 429) throw new Error('Too many requests. Please wait a moment and try again.');
    throw new Error(text || `Anthropic proxy error: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Streaming not supported by this browser.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by \n\n; each frame has lines like "event: X" and "data: {...}"
      let frameEnd;
      while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, frameEnd);
        buffer = buffer.slice(frameEnd + 2);

        // Extract the data: line(s)
        const dataLines = frame
          .split('\n')
          .filter(line => line.startsWith('data:'))
          .map(line => line.slice(5).trimStart());
        if (dataLines.length === 0) continue;
        const dataStr = dataLines.join('\n');
        if (dataStr === '[DONE]') continue;

        let evt;
        try { evt = JSON.parse(dataStr); }
        catch { continue; }

        // Only care about text deltas for the visible prompt text
        if (evt.type === 'content_block_delta' && evt.delta?.type === 'text_delta') {
          const delta = evt.delta.text || '';
          if (delta) {
            fullText += delta;
            if (onText) onText(delta);
          }
        }
        // (We ignore thinking_delta, message_delta, etc. — not needed for this use case)
      }
    }
  } finally {
    try { reader.releaseLock(); } catch {}
  }

  if (onComplete) onComplete(fullText);
  return fullText;
}
